import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAdminChinaAnalyticsApi, AdminChinaEnterpriseItem } from "../../features/analytics/api/adminAnalytics.api"
import { HBarChart, DonutChart, ChartCard } from "./Charts"
import { ChevronRightIcon, ExclamationTriangleIcon, BuildingOffice2Icon } from "@heroicons/react/16/solid"

const LIMIT = 5

const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  approved: { label: "Одобрено",    cls: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  pending:  { label: "На проверке", cls: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  rejected: { label: "Отклонено",   cls: "bg-red-50 text-red-600 ring-1 ring-red-200" },
}

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-[11px] ${i < Math.floor(rating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
    ))}
    <span className="ml-1 text-xs text-gray-500">{rating}</span>
  </span>
)

export const ChinaAnalyticsPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<AdminChinaEnterpriseItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getAdminChinaAnalyticsApi()
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить компании</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-52 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(items.length / LIMIT))
  const pageItems = items.slice((page - 1) * LIMIT, page * LIMIT)

  const approved = items.filter((e) => e.moderation_status === "approved").length
  const pending  = items.filter((e) => e.moderation_status === "pending").length
  const rejected = items.filter((e) => e.moderation_status === "rejected").length

  return (
    <div className="space-y-5">
      {items.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <BuildingOffice2Icon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Компаний пока нет</p>
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Топ-8 компаний по просмотрам">
              <HBarChart
                data={[...items].sort((a, b) => b.views - a.views).slice(0, 8).map((e) => ({ label: e.name.slice(0, 18), value: e.views }))}
                color="#ef4444"
              />
            </ChartCard>
            <ChartCard title="Статус модерации">
              <DonutChart data={[
                { label: "Одобрено",    value: approved, color: "#10b981" },
                { label: "На проверке", value: pending,  color: "#f59e0b" },
                { label: "Отклонено",   value: rejected, color: "#ef4444" },
              ]} />
            </ChartCard>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Компании Китая</h3>
              <span className="text-xs text-gray-400">{total} компаний</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/40">
                    {["Название", "Просмотры", "В избранном", "Отзывы", "Рейтинг", "Статус", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageItems.map((e) => {
                    const status = STATUS_CFG[e.moderation_status] ?? { label: e.moderation_status, cls: "bg-gray-100 text-gray-500" }
                    return (
                      <tr key={e.enterprise_id}
                        onClick={() => navigate(`/admin/analytics/china/${e.enterprise_id}`)}
                        className="hover:bg-blue-50/40 transition-colors cursor-pointer group">
                        <td className="px-4 py-3.5">
                          <p className="font-medium text-gray-800">{e.name}</p>
                        </td>
                        <td className="px-4 py-3.5 text-gray-700 font-medium">{e.views.toLocaleString("ru")}</td>
                        <td className="px-4 py-3.5 text-gray-600">{e.favorites_count}</td>
                        <td className="px-4 py-3.5 text-gray-600">{e.reviews_count}</td>
                        <td className="px-4 py-3.5"><Stars rating={e.avg_rating} /></td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Стр. <span className="font-medium text-gray-600">{page}</span> из{" "}
                <span className="font-medium text-gray-600">{totalPages}</span>
              </span>
              <div className="flex gap-1.5">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Пред.
                </button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  След. →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
